import Phaser from "phaser";
import { FONT_FAMILY } from "../ui/uiTokens.js";
import { DEPTH } from "./depthLayers.js";
import type { BrickCellKind } from "../data/ChapterData.js";

const MARK_STATUS_COLOR = 0xff4f8b;
const MARK_STATUS_FILL = 0x2a0617;
const MARK_STATUS_LABEL = "MARK";

export interface BrickMarkStatusBounds {
  readonly isMarked: boolean;
  readonly kind: BrickCellKind;
  readonly x: number;
  readonly y: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
}

export class BrickMarkStatus {
  readonly objects: Phaser.GameObjects.GameObject[];
  private readonly ring: Phaser.GameObjects.Graphics;
  private readonly label: Phaser.GameObjects.Text;
  private damageMul = 1;
  private hitsRemaining = 0;

  constructor(private readonly scene: Phaser.Scene) {
    this.ring = scene.add
      .graphics()
      .setDepth(DEPTH.brickLabel + 0.35)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    this.label = scene.add
      .text(0, 0, MARK_STATUS_LABEL, {
        fontFamily: FONT_FAMILY,
        fontSize: "8px",
        color: "#ffd7e6",
        stroke: "#3a061a",
        strokeThickness: 2,
      })
      .setOrigin(0.5)
      .setDepth(DEPTH.brickLabel + 0.4)
      .setVisible(false);
    this.objects = [this.ring, this.label];
  }

  get visible(): boolean {
    return this.ring.visible;
  }

  get isMarked(): boolean {
    return this.hitsRemaining > 0 && this.damageMul > 1;
  }

  get damageMultiplier(): number {
    return this.isMarked ? this.damageMul : 1;
  }

  clear(): void {
    this.scene.tweens.killTweensOf(this.objects);
    this.damageMul = 1;
    this.hitsRemaining = 0;
    this.ring.clear().setAlpha(1).setActive(false).setVisible(false);
    this.label.setAlpha(1).setScale(1).setActive(false).setVisible(false);
  }

  hideVisuals(): void {
    this.scene.tweens.killTweensOf(this.objects);
    this.ring.clear().setAlpha(1).setActive(false).setVisible(false);
    this.label.setAlpha(1).setScale(1).setActive(false).setVisible(false);
  }

  removeFromDisplayList(): void {
    this.ring.removeFromDisplayList();
    this.label.removeFromDisplayList();
  }

  draw(bounds: BrickMarkStatusBounds): void {
    if (!bounds.isMarked || bounds.kind === "mystery") {
      this.ring.clear().setActive(false).setVisible(false);
      this.label.setActive(false).setVisible(false);
      return;
    }
    const w = bounds.displayWidth;
    const h = bounds.displayHeight;
    const pad = Math.max(5, Math.min(w, h) * 0.16);
    const radius = Math.max(6, Math.min(w, h) * 0.18);
    const x = bounds.x - w / 2 - pad;
    const y = bounds.y - h / 2 - pad;
    this.ring
      .clear()
      .lineStyle(2, MARK_STATUS_COLOR, 0.96)
      .strokeRoundedRect(x, y, w + pad * 2, h + pad * 2, radius)
      .lineStyle(2, MARK_STATUS_COLOR, 0.88)
      .beginPath()
      .moveTo(bounds.x - radius * 0.8, bounds.y)
      .lineTo(bounds.x + radius * 0.8, bounds.y)
      .moveTo(bounds.x, bounds.y - radius * 0.8)
      .lineTo(bounds.x, bounds.y + radius * 0.8)
      .strokePath()
      .fillStyle(MARK_STATUS_FILL, 0.76)
      .fillRoundedRect(bounds.x - 17, y - 3, 34, 12, 4)
      .setAlpha(0.96)
      .setActive(true)
      .setVisible(true);
    this.label
      .setText(MARK_STATUS_LABEL)
      .setPosition(bounds.x, y + 3)
      .setActive(true)
      .setVisible(true);
  }

  apply(bounds: BrickMarkStatusBounds, damageMultiplier: number, hits: number): void {
    const mul = Math.max(1, damageMultiplier);
    const count = Math.floor(hits);
    if (mul <= 1 || count <= 0) return;
    this.damageMul = Math.max(this.damageMul, mul);
    this.hitsRemaining = Math.max(this.hitsRemaining, count);
    this.draw({ ...bounds, isMarked: this.isMarked });
    this.label.setScale(1.25);
    this.scene.tweens.add({
      targets: this.label,
      scale: 1,
      duration: 120,
      ease: "Back.easeOut",
    });
  }

  consume(bounds: BrickMarkStatusBounds): void {
    if (!this.isMarked) return;
    this.hitsRemaining -= 1;
    if (this.hitsRemaining <= 0) {
      this.clear();
      return;
    }
    this.draw({ ...bounds, isMarked: this.isMarked });
  }

  setMask(mask: Phaser.Display.Masks.GeometryMask): void {
    this.ring.setMask(mask);
    this.label.setMask(mask);
  }

  clearMask(): void {
    this.ring.clearMask();
    this.label.clearMask();
  }

  destroy(): void {
    this.ring.destroy();
    this.label.destroy();
  }
}
